import React from "react";
import {
    Backdrop,
    Box,
    CircularProgress,
    IconButton,
    Pagination,
    PaginationItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import {ArrowBackIosNew, ArrowForwardIos, FirstPage, LastPage} from "@mui/icons-material";
import BidPopover from "./BidPopover";
import Icons from "../../../../../components/DmpIcons/icons";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import CustomCheckbox from "../../../../../components/Checkbox";

const MultipliersTable = ({
    multipliers,
    page,
    onPageChange,
    handleDelete,
    platform,
    hotelOrGroupType,
    onUpdateMultiplierValue,
    selectedRows,
    setSelectedRows,
    loading,
    selectedTab
}) => {
    const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);
    const [selectedDelete, setSelectedDelete] = React.useState(null);
    const [isBulkDelete, setIsBulkDelete] = React.useState(false);
    const [sortConfig, setSortConfig] = React.useState({ key: null, direction: 'asc' });

    const rowsPerPage = 10;

    const cellStyle = (isLastRow, isBeginCell = false, isEndCell = false) => {
        let leftInset = 0;
        let rightInset = 0;

        if (isBeginCell === true) leftInset = 16;
        if (isEndCell === true) rightInset = 16;

        return {
            padding: "10px 16px",
            position: 'relative',
            borderBottom: 'none',
            '&::after': !isLastRow
                ? {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: `${leftInset}px`,
                    right: `${rightInset}px`,
                    height: '1px',
                    backgroundColor: '#E0E0E0',
                }
                : {},
        };
    };

    const headerColumns = [
        { label: `${hotelOrGroupType} Name`, key: 'label_entity', isBegin: true, isEnd: false },
        { label: "Market", key: 'label_market', isBegin: false, isEnd: false },
        { label: "Breakdown", key: 'label_breakdown', isBegin: false, isEnd: false },
        { label: "Multiplier Adjustment", key: 'multiplier_value', isBegin: false, isEnd: false },
        { label: "", key: null, isBegin: false, isEnd: true }
    ];

    const sortedMultipliers = React.useMemo(() => {
        const sorted = [...multipliers];
        if (sortConfig.key) {
            sorted.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                if (sortConfig.key === 'multiplier_value') {
                    aVal = parseFloat(aVal);
                    bVal = parseFloat(bVal);
                } else {
                    aVal = aVal?.toString().toLowerCase() || '';
                    bVal = bVal?.toString().toLowerCase() || '';
                }

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sorted;
    }, [multipliers, sortConfig]);

    const paginatedMultipliers = sortedMultipliers.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box style={{ position: "relative" }}>
            <Backdrop
                sx={{
                    color: '#fff',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: '0.25 !important',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
                open={loading?.multipliersMenu?.[selectedTab] === true}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <TableContainer sx={{
                backgroundColor: "white",
                marginTop: "0px",
                paddingTop: "0px",
                border: "none",
                boxShadow: "none",
                borderBottomLeftRadius: "10px",
                borderBottomRightRadius: "10px",
                marginBottom: "10px"
            }}>
                <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                    <TableHead>
                        <TableRow>
                            {headerColumns.map((col, index) => (
                                <TableCell
                                    key={index}
                                    sx={{
                                        ...cellStyle(false, col.isBegin, col.isEnd),
                                        textTransform: 'capitalize',
                                        cursor: col.key ? 'pointer' : 'default'
                                    }}
                                    onClick={() => {
                                        if (!col.key) return;
                                        setSortConfig((prev) => ({
                                            key: col.key,
                                            direction:
                                                prev.key === col.key && prev.direction === 'asc'
                                                    ? 'desc'
                                                    : 'asc'
                                        }));
                                    }}
                                >
                                    <strong>
                                        {col.label}
                                        {col.key && sortConfig.key === col.key && (
                                            <span style={{ marginLeft: 4 }}>
                                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </strong>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedMultipliers.length > 0 ? (
                            paginatedMultipliers.map((item, index) => {
                                const isLast = index === paginatedMultipliers.length - 1;
                                const isChecked = selectedRows.includes(item.bid_id);

                                return (
                                    <TableRow key={index}>
                                        <TableCell sx={cellStyle(isLast, true)}>
                                            {
                                                item.can_edit === true ? (
                                                    <CustomCheckbox
                                                        checked={isChecked}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedRows((prev) => [...prev, item.bid_id]);
                                                            } else {
                                                                setSelectedRows((prev) =>
                                                                    prev.filter((id) => id !== item.bid_id)
                                                                );
                                                            }
                                                        }}
                                                        title={item.label_entity}
                                                        checkboxStyles={{ marginRight: "8px" }}
                                                    />
                                                ) : `${item.label_entity}`
                                            }
                                        </TableCell>
                                        <TableCell sx={cellStyle(isLast)}>{item.label_market}</TableCell>
                                        <TableCell sx={cellStyle(isLast)}>{item.label_breakdown}</TableCell>
                                        <TableCell sx={cellStyle(isLast)}>
                                            <BidPopover
                                                bid={{
                                                    bid_label: `${item.multiplier_value}%`,
                                                    bid_symbol: "%",
                                                    bid_editable: item.can_edit === true ,
                                                    bid_edit_in_place_title: item?.bid_edit_in_place_title,
                                                    bid_history_path: item?.bid_history_path,
                                                    bid_value_edit_path: item?.bid_value_edit_path,
                                                    platform: platform
                                                }}
                                                onUpdateBidValue={onUpdateMultiplierValue}
                                            />
                                        </TableCell>
                                        <TableCell sx={cellStyle(isLast, false, true)}>
                                            {item.can_edit === true ? (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedDelete(item.bid_id);
                                                        setIsBulkDelete(false);
                                                        setOpenConfirmDialog(true);
                                                    }}
                                                >
                                                    <img
                                                        src={Icons.ActionsIconsSVG.DeleteIconSVG}
                                                        alt="Delete"
                                                        className="icon"
                                                        style={{
                                                            paddingLeft: "40%",
                                                            width: "10px",
                                                            height: "12px",
                                                        }}
                                                    />
                                                </IconButton>
                                            ) : ""}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center">No data available.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack
                direction="row"
                spacing={1}
                sx={{
                    justifyContent: "space-between",
                    width: "100%",
                    marginTop: "10px",
                }}
            >
                <IconButton
                    size="small"
                    disabled={selectedRows.length === 0}
                    sx={{
                        backgroundColor: "#61D5A9",
                        borderRadius: "10px",
                        fontWeight: 400,
                        fontSize: '10px',
                        fontStyle: 'Roboto',
                        '&:hover': {
                            backgroundColor: "#61D5A9",
                        },
                    }}
                    onClick={() => {
                        setIsBulkDelete(true);
                        setOpenConfirmDialog(true);
                    }}
                >
                    Delete Selected
                </IconButton>

                <Pagination
                    count={Math.ceil(multipliers.length / rowsPerPage)}
                    page={page + 1}
                    onChange={(event, value) => onPageChange(event, value - 1)}
                    siblingCount={1}
                    boundaryCount={1}
                    showFirstButton
                    showLastButton
                    size="small"
                    renderItem={(item) => (
                        <PaginationItem
                            components={{
                                first: FirstPage,
                                last: LastPage,
                                previous: ArrowBackIosNew,
                                next: ArrowForwardIos,
                            }}
                            {...item}
                            sx={{
                                fontSize: "10px",
                                height: "14px",
                                minWidth: "24px",
                                borderRadius: "0",
                                color: "#454545",
                                '& svg': { width: "10px" },
                                '&.Mui-selected': {
                                    backgroundColor: "transparent",
                                    fontWeight: 600,
                                    border: "none",
                                    boxShadow: "none",
                                },
                                '&.Mui-selected:hover': { backgroundColor: "transparent" }
                            }}
                        />
                    )}
                />
            </Stack>

            <ConfirmDeleteModal
                open={openConfirmDialog}
                onClose={() => {
                    setOpenConfirmDialog(false);
                    setIsBulkDelete(false);
                }}
                onConfirm={async () => {
                    setOpenConfirmDialog(false);
                    if (isBulkDelete) {
                        await handleDelete(selectedRows);
                        setSelectedRows([]);
                    } else if (selectedDelete) {
                        await handleDelete(selectedDelete);
                    }
                    setIsBulkDelete(false);
                }}
                message="Are you sure you want to delete this multiplier?"
            />
        </Box>
    );
};

export default MultipliersTable;